<template>
  <BaseModal
    :visible="isVisible"
    :show-footer="false"
    :closable="false"
    size="medium"
    :show-header="false"
    variant="info"
  >
    <!-- Custom Header with Spinner -->
    <div class="custom-modal-header">
      <div class="header-spinner">
        <div class="spinner-ring"></div>
      </div>
      <h3 class="modal-title">{{ modalTitle }}</h3>
    </div>

    <!-- Description -->
    <p class="enhancement-description">
      {{ modalDescription }}
    </p>

    <!-- Progress Bar -->
    <div class="progress-container">
      <div class="progress-bar">
        <div 
          class="progress-fill" 
          :style="{ width: progress + '%' }"
        ></div>
      </div>
      <div class="progress-text">{{ progress }}%</div>
    </div>

    <!-- Status Messages for Brand Analysis -->
    <div v-if="analysisType !== 'text-generation' && analysisType !== 'text-regeneration-email' && analysisType !== 'text-regeneration-appPages'" class="status-messages">
      <div class="status-message" :class="{ active: progress >= 10 }">
        <span class="status-icon">✓</span>
        Analyzing image or URL...
      </div>
      <div class="status-message" :class="{ active: progress >= 30 }">
        <span class="status-icon">✓</span>
        Extracting dominant colors...
      </div>
      <div class="status-message" :class="{ active: progress >= 50 }">
        <span class="status-icon">✓</span>
        Analyzing font characteristics...
      </div>
      <div class="status-message" :class="{ active: progress >= 70 }">
        <span class="status-icon">✓</span>
        Generating email templates...
      </div>
      <div class="status-message" :class="{ active: progress >= 90 }">
        <span class="status-icon">✓</span>
        Generating game pages...
      </div>
    </div>

    <!-- Status Messages for Text Generation (Initial Generation) -->
    <div v-if="analysisType === 'text-generation'" class="status-messages">
      <div class="status-message" :class="{ active: progress >= 10 }">
        <span class="status-icon">✓</span>
        Analyzing campaign details...
      </div>
      <div class="status-message" :class="{ active: progress >= 30 }">
        <span class="status-icon">✓</span>
        Generating email subject and title...
      </div>
      <div class="status-message" :class="{ active: progress >= 50 }">
        <span class="status-icon">✓</span>
        Writing email body content...
      </div>
      <div class="status-message" :class="{ active: progress >= 70 }">
        <span class="status-icon">✓</span>
        Creating landing page texts...
      </div>
      <div class="status-message" :class="{ active: progress >= 90 }">
        <span class="status-icon">✓</span>
        Finalizing game page content...
      </div>
    </div>

    <!-- Status Messages for Email Regeneration -->
    <div v-else-if="analysisType === 'text-regeneration-email'" class="status-messages">
      <div class="status-message" :class="{ active: progress >= 10 }">
        <span class="status-icon">✓</span>
        Analyzing current email content...
      </div>
      <div class="status-message" :class="{ active: progress >= 30 }">
        <span class="status-icon">✓</span>
        Regenerating email subject...
      </div>
      <div class="status-message" :class="{ active: progress >= 50 }">
        <span class="status-icon">✓</span>
        Regenerating email title...
      </div>
      <div class="status-message" :class="{ active: progress >= 70 }">
        <span class="status-icon">✓</span>
        Regenerating email body...
      </div>
      <div class="status-message" :class="{ active: progress >= 90 }">
        <span class="status-icon">✓</span>
        Finalizing email texts...
      </div>
    </div>

    <!-- Status Messages for App Pages Regeneration -->
    <div v-else-if="analysisType === 'text-regeneration-appPages'" class="status-messages">
      <div class="status-message" :class="{ active: progress >= 10 }">
        <span class="status-icon">✓</span>
        Analyzing current app pages content...
      </div>
      <div class="status-message" :class="{ active: progress >= 30 }">
        <span class="status-icon">✓</span>
        Regenerating landing page texts...
      </div>
      <div class="status-message" :class="{ active: progress >= 50 }">
        <span class="status-icon">✓</span>
        Regenerating game page texts...
      </div>
      <div class="status-message" :class="{ active: progress >= 70 }">
        <span class="status-icon">✓</span>
        Regenerating won and lost page texts...
      </div>
      <div class="status-message" :class="{ active: progress >= 90 }">
        <span class="status-icon">✓</span>
        Finalizing all app pages...
      </div>
    </div>
  </BaseModal>
</template>

<script>
import BaseModal from '../common/BaseModal.vue'

export default {
  name: 'EnhancementProgressModal',
  components: {
    BaseModal
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    progress: {
      type: Number,
      default: 0
    },
    analysisType: {
      type: String,
      default: 'template',
      validator: (value) => ['template', 'image', 'app-theme', 'theme', 'text-generation', 'text-regeneration-email', 'text-regeneration-appPages'].includes(value)
    },
    currentPhase: {
      type: String,
      default: 'analyze',
      validator: (value) => ['analyze', 'generate', ''].includes(value)
    }
  },
  computed: {
    modalTitle() {
      if (this.analysisType === 'text-generation') {
        return 'AI Content Generation'
      }
      if (this.analysisType === 'text-regeneration-email') {
        return 'Regenerating Email Texts'
      }
      if (this.analysisType === 'text-regeneration-appPages') {
        return 'Regenerating App Pages Texts'
      }
      return 'AI Brand Analysis'
    },
    modalDescription() {
      if (this.analysisType === 'text-generation') {
        return 'AI is generating personalized content for your email templates and app pages...'
      }
      if (this.analysisType === 'text-regeneration-email') {
        return 'AI is regenerating your email template texts based on your preferences...'
      }
      if (this.analysisType === 'text-regeneration-appPages') {
        return 'AI is regenerating all app pages texts based on your preferences...'
      }
      return 'AI is analyzing your brand and generating email templates and game pages...'
    }
  }
}
</script>

<style scoped>
/* Custom Modal Header */
.custom-modal-header {
  display: flex;
  align-items: start;
  justify-content: start;
  gap: 16px;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.header-spinner {
  display: flex;
  align-items: start;
  justify-content: start;
}

.spinner-ring {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.2;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Dark mode support for custom header */
@media (prefers-color-scheme: dark) {
  .custom-modal-header {
    border-color: #374151;
  }
  
  .modal-title {
    color: #f9fafb;
  }
  
  .spinner-ring {
    border-color: #374151;
    border-top-color: #3b82f6;
  }
}
</style>

<style scoped>
.enhancement-description {
  font-size: 16px;
  color: #718096;
  margin: 0 0 32px 0;
  line-height: 1.5;
  text-align: center;
}

.progress-container {
  margin-bottom: 32px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 12px;
}

.progress-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 4px;
  transition: width 0.3s ease-out;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: shimmer 1.5s infinite;
}

.progress-text {
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  text-align: center;
}

.status-messages {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}

.status-message {
  display: flex;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
  color: #a0aec0;
  transition: color 0.3s ease;
}

.status-message.active {
  color: #38a169;
}

.status-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 10px;
  color: transparent;
  transition: all 0.3s ease;
}

.status-message.active .status-icon {
  background-color: #38a169;
  color: white;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .enhancement-description {
    color: #d1d5db;
  }

  .progress-bar {
    background-color: #374151;
  }

  .progress-text {
    color: #d1d5db;
  }

  .status-message {
    color: #6b7280;
  }

  .status-message.active {
    color: #34d399;
  }

  .status-icon {
    background-color: #374151;
  }

  .status-message.active .status-icon {
    background-color: #34d399;
  }
}
</style>
